import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Pause, Play, RotateCcw, TrendingUp, Zap } from 'lucide-react';
import { readLiveData, parseLiveData, parseO2 } from '../lib/serial';
import { setLive as pushLiveStore } from '../lib/liveStore';

const POLL_OPTIONS = [300, 500, 1000];

const RPM_MAX = 15000;
const START = 150, SWEEP = 240; // degrees: 0 at lower-left, max at lower-right

const METRICS = {
  rpm: { label: 'Engine RPM', unit: 'rpm', max: 15000, min: 0, dec: 0, color: '#00F59B' },
  ect: { label: 'Engine/Oil Temp', unit: '°C', max: 150, min: -40, dec: 2, color: '#FFB800' },
  tps: { label: 'Throttle', unit: '%', max: 100, min: 0, dec: 2, color: '#00E5FF' },
  iat: { label: 'Intake Air Temp', unit: '°C', max: 120, min: -40, dec: 2, color: '#00E5FF' },
  map: { label: 'MAP', unit: 'kPa', max: 255, min: 0, dec: 0, color: '#A78BFA' },
  battery: { label: 'Battery Voltage', unit: 'V', max: 16, min: 0, dec: 3, color: '#00F59B' },
  o2: { label: 'O2 Sensor', unit: 'V', max: 5, min: 0, dec: 2, color: '#A78BFA' },
  injector: { label: 'Injector', unit: 'ms', max: 30, min: 0, dec: 2, color: '#FF6b63' },
  ignition: { label: 'Ign Advance', unit: '°', max: 60, min: 0, dec: 1, color: '#FFB800' },
  speed: { label: 'Speed', unit: 'km/h', max: 200, min: 0, dec: 0, color: '#00E5FF' },
};
const TABLE_ROWS = ['ect', 'tps', 'iat', 'map', 'battery', 'o2', 'injector', 'ignition', 'speed'];

// Vertical bar gauges for the classic cluster (matches the reference dashboard look).
const BAR_GAUGES = [
  { key: 'ect', label: 'ECT', unit: '°C', min: 0, max: 140, color: '#FF6b63', dec: 0 },
  { key: 'iat', label: 'IAT', unit: '°C', min: 0, max: 60, color: '#00E5FF', dec: 0 },
  { key: 'map', label: 'MAP', unit: 'kPa', min: 0, max: 120, color: '#A78BFA', dec: 0 },
  { key: 'ignition', label: 'IGN', unit: '°', min: 0, max: 56, color: '#FFB800', dec: 1 },
  { key: 'injector', label: 'INJ', unit: 'ms', min: 0, max: 16, color: '#00F59B', dec: 2 },
  { key: 'battery', label: 'Battery', unit: 'V', min: 8, max: 16, color: '#FFC400', dec: 1 },
];

const pol = (cx, cy, r, deg) => { const a = (deg * Math.PI) / 180; return [cx + r * Math.cos(a), cy + r * Math.sin(a)]; };
const angleOf = (v) => START + Math.max(0, Math.min(1, v / RPM_MAX)) * SWEEP;
const arcPath = (cx, cy, r, d1, d2) => {
  const [x1, y1] = pol(cx, cy, r, d1); const [x2, y2] = pol(cx, cy, r, d2);
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${(d2 - d1) > 180 ? 1 : 0} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
};

const AnalogTach = ({ value, peak, redline, alert, dur = 300, afr = null }) => {
  const cx = 180, cy = 180;
  const has = value != null && Number.isFinite(value);
  const target = has ? Math.max(0, Math.min(RPM_MAX, value)) : 0;
  const [rpm, setRpm] = useState(0);
  const rpmRef = useRef(0);
  useEffect(() => {
    const from = rpmRef.current;
    const to = target;
    if (from === to) return;
    const span = Math.max(120, Math.min(dur, 600)); // sweep over ~one poll interval
    const t0 = performance.now();
    let raf;
    const step = (now) => {
      const k = Math.min(1, (now - t0) / span);
      const v = from + (to - from) * k;
      rpmRef.current = v; setRpm(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  const ticks = useMemo(() => {
    const arr = [];
    for (let v = 0; v <= RPM_MAX; v += 250) {
      const major = v % 1000 === 0;
      const a = angleOf(v);
      const [x1, y1] = pol(cx, cy, 150, a);
      const [x2, y2] = pol(cx, cy, major ? 130 : 140, a);
      const [lx, ly] = pol(cx, cy, 113, a);
      arr.push({ v, major, x1, y1, x2, y2, lx, ly });
    }
    return arr;
  }, []);
  const na = angleOf(rpm);
  const tip = pol(cx, cy, 132, na);
  const bl = pol(cx, cy, 11, na + 90);
  const br = pol(cx, cy, 11, na - 90);
  const tail = pol(cx, cy, 30, na + 180);
  const showPeak = peak != null && Number.isFinite(peak) && peak > 0;
  const pa = angleOf(peak || 0);
  const p1 = pol(cx, cy, 152, pa), p2 = pol(cx, cy, 128, pa);
  const ra = angleOf(redline || 0);
  const r1 = pol(cx, cy, 156, ra), r2 = pol(cx, cy, 124, ra);
  return (
    <svg viewBox="0 0 360 350" className="w-full h-full" data-testid="gauge-rpm">
      <defs>
        <radialGradient id="bezelDk" cx="50%" cy="35%" r="75%">
          <stop offset="70%" stopColor="#232c3b" /><stop offset="92%" stopColor="#161d29" /><stop offset="100%" stopColor="#0a0e16" />
        </radialGradient>
        <filter id="needleGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor={alert ? '#E53935' : '#00E5FF'} floodOpacity="0.7" />
        </filter>
        <filter id="ringGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={alert ? '#E53935' : '#00E5FF'} floodOpacity="0.55" />
        </filter>
      </defs>
      <circle cx={cx} cy={cy} r="172" fill="url(#bezelDk)" stroke="#0a0e16" strokeWidth="1" />
      <circle cx={cx} cy={cy} r="169" fill="none" stroke={alert ? '#E53935' : '#00E5FF'} strokeWidth="1.4" opacity="0.55" filter="url(#ringGlow)" />
      <circle cx={cx} cy={cy} r="158" fill="#0c111b" stroke={alert ? '#E53935' : '#124b5e'} strokeWidth="1.5" />
      {alert && <circle cx={cx} cy={cy} r="152" fill="#E53935" opacity="0.18" className="animate-pulse" />}
      {alert && <circle cx={cx} cy={cy} r="156" fill="none" stroke="#E53935" strokeWidth="6" className="animate-pulse" />}
      <path d={arcPath(cx, cy, 150, angleOf(RPM_MAX * 0.8), angleOf(RPM_MAX * 0.9))} fill="none" stroke="#F5A623" strokeWidth="9" />
      <path d={arcPath(cx, cy, 150, angleOf(RPM_MAX * 0.9), angleOf(RPM_MAX))} fill="none" stroke="#E53935" strokeWidth="9" />
      {ticks.map((t) => (
        <g key={t.v}>
          <line x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke={t.major ? '#aab6c6' : '#3a4658'} strokeWidth={t.major ? 2.4 : 1.2} strokeLinecap="round" />
          {t.major && <text x={t.lx} y={t.ly + 5} textAnchor="middle" fontSize="16" fontWeight="700" fill="#c7d0dc" fontFamily="Arial, sans-serif">{t.v / 1000}</text>}
        </g>
      ))}
      <text x={cx} y={cy - 34} textAnchor="middle" fontSize="12" fontWeight="700" fill="#8b93a3" letterSpacing="1" fontFamily="Arial, sans-serif">× 1000</text>
      <text x={cx} y={cy + 20} textAnchor="middle" fontSize="19" fontWeight="700" fill="#cdd6e2" fontFamily="Arial, sans-serif">Engine RPM</text>
      <rect x={cx - 62} y={cy + 34} width="124" height="34" rx="4" fill="#05070A" stroke={alert ? '#E53935' : '#0e5266'} strokeWidth="1.5" />
      <text x={cx} y={cy + 57} textAnchor="middle" fontSize="20" fontWeight="700" fill={alert ? '#FF6b63' : '#00F59B'} fontFamily="ui-monospace, monospace">{has ? `${Math.round(rpm)} rpm` : '-- rpm'}</text>
      <rect x={cx - 52} y={cy + 76} width="104" height="30" rx="4" fill="#05070A" stroke="#1E2A42" strokeWidth="1.2" />
      <text x={cx - 40} y={cy + 96} textAnchor="start" fontSize="12" fontWeight="700" fill="#8b93a3" fontFamily="Arial, sans-serif">AFR</text>
      <text x={cx + 40} y={cy + 96} textAnchor="end" fontSize="16" fontWeight="700" fill={afr == null ? '#64748b' : afr < 13.5 ? '#FF6b63' : afr > 15.6 ? '#00E5FF' : '#00F59B'} fontFamily="ui-monospace, monospace">{afr != null ? afr.toFixed(1) : '--'}</text>
      {redline > 0 && <line x1={r1[0]} y1={r1[1]} x2={r2[0]} y2={r2[1]} stroke="#E53935" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="3 2" />}
      {showPeak && <line x1={p1[0]} y1={p1[1]} x2={p2[0]} y2={p2[1]} stroke="#FFB800" strokeWidth="3" strokeLinecap="round" />}
      <polygon points={`${tip[0]},${tip[1]} ${bl[0]},${bl[1]} ${tail[0]},${tail[1]} ${br[0]},${br[1]}`} fill={alert ? '#E53935' : '#00E5FF'} filter="url(#needleGlow)" style={{ transition: 'fill 0.12s ease' }} />
      <circle cx={cx} cy={cy} r="14" fill="#2a3344" />
      <circle cx={cx} cy={cy} r="6" fill="#0a0e16" />
    </svg>
  );
};

const LiveChart = ({ history, metricKey }) => {
  const m = METRICS[metricKey];
  const W = 520, H = 200, padL = 46, padB = 28, padT = 12, padR = 12;
  const now = Date.now();
  const WINDOW = 12000;
  const pts = history.filter((s) => now - s.t <= WINDOW && s[metricKey] != null && Number.isFinite(s[metricKey]));
  const maxY = m.max, minY = Math.max(0, m.min);
  const x = (t) => padL + ((WINDOW - (now - t)) / WINDOW) * (W - padL - padR);
  const y = (v) => padT + (1 - (v - minY) / (maxY - minY)) * (H - padT - padB);
  const line = pts.map((s, i) => `${i ? 'L' : 'M'} ${x(s.t).toFixed(1)} ${y(s[metricKey]).toFixed(1)}`).join(' ');
  const area = pts.length ? `${line} L ${x(pts[pts.length - 1].t).toFixed(1)} ${H - padB} L ${x(pts[0].t).toFixed(1)} ${H - padB} Z` : '';
  const yTicks = [maxY, (maxY + minY) / 2, minY];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="livedata-chart">
      <rect x={padL} y={padT} width={W - padL - padR} height={H - padT - padB} fill="#0B0F19" stroke="#1E2A42" />
      {yTicks.map((v) => (
        <g key={`y${v}`}>
          <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} stroke="#182131" />
          <text x={padL - 6} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#6b7688" fontFamily="Arial">{Math.round(v)}</text>
        </g>
      ))}
      {[12, 9, 6, 3, 0].map((s) => (
        <text key={`x${s}`} x={padL + ((WINDOW - s * 1000) / WINDOW) * (W - padL - padR)} y={H - 8} textAnchor="middle" fontSize="11" fill="#6b7688" fontFamily="Arial">{s}</text>
      ))}
      {area && <path d={area} fill={`${m.color}22`} />}
      {line && <path d={line} fill="none" stroke={m.color} strokeWidth="2" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 3px ${m.color}66)` }} />}
      <text x={padL - 34} y={H / 2} textAnchor="middle" fontSize="11" fill="#8a97a8" fontFamily="Arial" transform={`rotate(-90 ${padL - 34} ${H / 2})`}>{m.label} ({m.unit})</text>
    </svg>
  );
};

const fmt = (v, dec) => (v == null || !Number.isFinite(v)) ? '--' : v.toFixed(dec);
const THEMES = {
  cyan: { name: 'Cyan', accent: '#22d3ee', sweep: '#C9E265', bar: '#2f81f7' },
  race: { name: 'Race Red', accent: '#FF453A', sweep: '#FF7a3c', bar: '#FF453A' },
  green: { name: 'Green', accent: '#00F59B', sweep: '#C9E265', bar: '#00F59B' },
  amber: { name: 'Amber', accent: '#FFB800', sweep: '#FFC400', bar: '#FFB800' },
};
const winHead = 'flex items-center justify-between px-3 py-2 bg-gradient-to-r from-[#0B1322] to-[#0B0F19] border-b border-[#123247]';
const winCard = 'rounded-xl bg-[#0d121d] border border-[#153348] overflow-hidden shadow-[0_0_0_1px_rgba(0,229,255,0.07),0_0_30px_-12px_rgba(0,229,255,0.5)]';
const titleBar = 'text-sm font-semibold text-slate-100 flex items-center gap-2 before:content-[""] before:h-4 before:w-1 before:rounded-full before:bg-[#00E5FF] before:shadow-[0_0_8px_1px_rgba(0,229,255,0.7)]';

// AiM-style digital dashboard: LED shift bar + big RPM + RPM bargraph + sensor tiles.
const AimDash = ({ d = {}, o2, redline = 9000, live, peakRpm, theme = { accent: '#22d3ee', sweep: '#C9E265', bar: '#2f81f7' } }) => {
  const SEG = 12;
  const rpm = Number.isFinite(d.rpm) ? d.rpm : null;
  const f = redline > 0 && rpm != null ? Math.min(1.06, rpm / redline) : 0;
  const lit = Math.round(f * SEG);
  const over = rpm != null && redline > 0 && rpm >= redline;
  const segColor = (i) => {
    const fr = (i + 1) / SEG;
    if (over) return '#B65CFF';
    if (fr < 0.55) return '#00F59B';
    if (fr < 0.75) return '#FFC400';
    if (fr < 0.9) return '#FF7A1A';
    return '#E53935';
  };
  const rmax = Math.max((redline || 0) + 1000, 10000);
  const barFrac = rpm != null ? Math.min(1, rpm / rmax) : 0;
  const tiles = [
    { label: 'MAP / BOOST', v: d.map, unit: 'kPa', dec: 0, min: 0, max: 250 },
    { label: 'THROTTLE', v: d.tps, unit: '%', dec: 0, min: 0, max: 100 },
    { label: 'OIL / ENG TEMP', v: d.ect, unit: '°C', dec: 0, min: 0, max: 150 },
    { label: 'INTAKE AIR', v: d.iat, unit: '°C', dec: 0, min: 0, max: 120 },
    { label: 'LAMBDA / O2', v: o2 && o2.o2v, unit: 'V', dec: 2, min: 0, max: 5 },
    { label: 'BATTERY VOLTAGE', v: d.battery, unit: 'V', dec: 1, min: 8, max: 16 },
  ];
  return (
    <div className="p-3 bg-gradient-to-b from-[#05070A] to-[#0a0e16]" data-testid="aim-dash">
      {/* Round LED shift lights */}
      <div className="flex justify-center gap-2 mb-3">
        {Array.from({ length: SEG }).map((_, i) => (
          <div key={i} className={`h-4 w-4 rounded-full ${over ? 'animate-pulse' : ''}`}
            style={{ background: i < lit ? segColor(i) : '#121826', border: '1px solid #1E2A42', boxShadow: i < lit ? `0 0 8px ${segColor(i)}` : 'none', transition: 'background 0.1s' }} />
        ))}
      </div>
      {/* Diagonal RPM sweep · big RPM · throttle */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex items-end gap-[3px] h-16 w-[170px] shrink-0" data-testid="aim-sweep">
          {Array.from({ length: 16 }).map((_, i) => {
            const on = rpm != null && (i / 16) < barFrac;
            return <div key={i} className="flex-1 rounded-t-[2px]" style={{ height: `${28 + i * 4.6}%`, background: on ? (over ? '#B65CFF' : theme.sweep) : '#141b28', transition: 'background 0.1s' }} />;
          })}
          {peakRpm != null && Number.isFinite(peakRpm) && peakRpm > 0 && (
            <div className="absolute top-0 bottom-0 w-[2px] bg-white/90" style={{ left: `${Math.min(100, (peakRpm / rmax) * 100)}%`, boxShadow: '0 0 6px #fff' }} data-testid="aim-rpm-peak" title={`Peak ${Math.round(peakRpm)} rpm`} />
          )}
        </div>
        <div className="flex items-baseline gap-2 flex-1 justify-center">
          <span className="text-[13px] font-bold text-slate-500 mb-3">RPM</span>
          <span className="font-mono font-black tabular-nums leading-none text-[72px]" style={{ color: over ? '#B65CFF' : theme.accent, textShadow: over ? '0 0 20px #B65CFF88' : `0 0 18px ${theme.accent}44` }} data-testid="aim-rpm">{rpm != null ? Math.round(rpm) : '----'}</span>
        </div>
        <div className="flex items-baseline gap-2 shrink-0">
          <span className="text-[13px] font-bold text-slate-500 mb-3">THR</span>
          <span className="font-mono font-black tabular-nums leading-none text-[52px]" style={{ color: (d.tps == null || !Number.isFinite(d.tps)) ? '#64748b' : '#f2f6fb' }}>{fmt(d.tps, 0)}</span>
          <span className="text-slate-500 font-bold text-base mb-3">%</span>
        </div>
      </div>
      {/* Sensor tiles — orange label · big value · blue mini bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="aim-tiles">
        {tiles.map((t) => {
          const off = t.v == null || !Number.isFinite(t.v);
          const frac = off ? 0 : Math.max(0, Math.min(1, (t.v - t.min) / ((t.max - t.min) || 1)));
          return (
            <div key={t.label} className="rounded-lg bg-[#0B0F19] border border-[#1E2A42] px-3 py-2" data-testid={`aim-tile-${t.label.replace(/[^a-zA-Z0-9]/g, '')}`}>
              <div className="text-[11px] font-bold text-[#FF7A3C] mb-0.5">{t.label}</div>
              <div className="font-mono font-bold text-[28px] leading-none text-white">
                {fmt(t.v, t.dec)}<span className="text-xs text-slate-500 ml-1 font-sans">{t.unit}</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-[#0a0e16] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${frac * 100}%`, background: theme.bar, transition: 'width 0.2s ease-out' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Reusable gauge cluster (RPM · TPS · OG AFR dials + ECT/IAT/MAP/IGN/INJ/Battery bars).
// `data` = live values (rpm/tps/map/ect/iat/battery/injector/ignition + o2 voltage).
export const GaugeCluster = ({ data, redline = 9000, interval = 300, live = false, compact = false, afr = null }) => {
  const d = data || {};
  const afrVal = (afr != null && Number.isFinite(afr)) ? Math.max(9, Math.min(18, afr)) : ((d.o2 != null && Number.isFinite(d.o2)) ? Math.max(9, Math.min(18, 14.7 - (d.o2 - 0.45) * 6)) : null);
  const overRedline = d.rpm != null && Number.isFinite(d.rpm) && redline > 0 && d.rpm >= redline;
  const items = [
    { label: 'RPM', v: d.rpm, unit: '', dec: 0, color: overRedline ? '#E53935' : '#00F59B' },
    { label: 'TPS', v: d.tps, unit: '%', dec: 0, color: '#00E5FF' },
    { label: 'AFR', v: afrVal, unit: '', dec: 1, color: afrVal == null ? '#64748b' : afrVal < 13.5 ? '#FF6b63' : afrVal > 15.6 ? '#00E5FF' : '#00F59B' },
    ...BAR_GAUGES.map((g) => ({ label: g.label === 'Battery' ? 'BAT' : g.label, v: d[g.key], unit: g.unit, dec: g.dec, color: g.color })),
  ];
  return (
    <div className={winCard} data-testid="gauge-cluster-window">
      <div className="flex flex-wrap items-stretch gap-2 px-2.5 py-2">
        <span className={`inline-flex items-center justify-center gap-1.5 shrink-0 text-[12px] font-bold px-2.5 py-1.5 rounded ${live ? 'text-[#00F59B] bg-[#00F59B]/10' : 'text-slate-500 bg-slate-500/10'}`} data-testid="cluster-status">
          <span className={`h-2 w-2 rounded-full ${live ? 'bg-[#00F59B] animate-pulse' : 'bg-slate-600'}`} />
          {live ? 'LIVE' : 'OFFLINE'}
        </span>
        {items.map((it) => {
          const has = it.v != null && Number.isFinite(it.v);
          return (
            <div key={it.label} className="flex flex-1 min-w-[92px] items-baseline justify-center gap-1.5 rounded-md bg-[#05070A] border border-[#1E2A42] px-2.5 py-1.5" data-testid={`digi-${it.label}`}>
              <span className="text-[11px] font-bold uppercase tracking-wide shrink-0" style={{ color: it.color }}>{it.label}</span>
              <span className="font-mono text-[15px] sm:text-[17px] font-bold text-slate-100 tabular-nums leading-none" data-testid={`digi-${it.label}-val`}>{fmt(it.v, it.dec)}</span>
              {it.unit && <span className="text-[10px] text-slate-500 shrink-0">{it.unit}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const LiveData = ({ manager, connected, busy, active, view = 'gauge' }) => {
  const [data, setData] = useState(null);
  const [live, setLive] = useState(false);
  const [noResp, setNoResp] = useState(false);
  const [paused, setPaused] = useState(false);
  const [chartKey, setChartKey] = useState('battery');
  const [peaks, setPeaks] = useState({});
  const [redline, setRedline] = useState(9000);
  const [raw, setRaw] = useState(null);
  const [rawExtra, setRawExtra] = useState(null);
  const [interval, setInterval] = useState(300);
  const [scanAddr, setScanAddr] = useState(0);
  const [theme, setTheme] = useState('cyan');
  const [, setTick] = useState(0);
  const pausedRef = useRef(false);
  const intervalRef = useRef(300);
  const scanRef = useRef(0);
  const histRef = useRef([]);
  const o2vRef = useRef(null);
  const stftRef = useRef(null);
  const [o2on, setO2on] = useState(true);
  const [o2, setO2] = useState(null);
  const o2Ref = useRef(true);
  pausedRef.current = paused;
  intervalRef.current = interval;
  scanRef.current = scanAddr;
  o2Ref.current = o2on;

  const streaming = active && connected && !busy && !manager.simulated;

  useEffect(() => {
    if (!streaming) { setLive(false); return; }
    let stop = false; let timer; let n = 0;
    const tick = async () => {
      if (stop) return;
      if (pausedRef.current) { timer = setTimeout(tick, 350); return; }
      // Poll ONE sensor block per tick (reference-style, single K-Line read = no engine
      // stutter). O2 / fuel-trim + the optional scan block change slowly, so fetch them
      // only every 12th tick — a second read per tick is what loaded the ECU and made it
      // hesitate ("gas brebet"). The main block already carries an O2 byte for AFR.
      const doExtra = (n % 6 === 0);
      try {
        const res = await readLiveData(manager, undefined, {
          scanAddr: doExtra ? (scanRef.current || null) : null,
          withO2: doExtra ? o2Ref.current : false,
        });
        if (res) {
          const p = res.parsed || parseLiveData(res.data);
          const oData = doExtra && res.o2 ? (res.o2.diag || parseO2(res.o2.data)) : null;
          if (oData) { if (oData.o2v != null) o2vRef.current = oData.o2v; if (oData.stft != null) stftRef.current = oData.stft; }
          p.o2 = o2vRef.current; p.stft = stftRef.current;
          setData(p); setNoResp(false); setLive(true);
          pushLiveStore(p);
          setRaw({ addr: res.addr, bytes: Array.from(res.raw || res.data) });
          if (doExtra) {
            setRawExtra(res.extra ? { addr: res.extra.addr, bytes: Array.from(res.extra.data) } : null);
            setO2(oData || null);
          }
          histRef.current.push({ t: Date.now(), ...p });
          if (histRef.current.length > 240) histRef.current.shift();
          setPeaks((prev) => {
            const next = { ...prev };
            for (const k of Object.keys(METRICS)) {
              const v = p[k];
              if (v != null && Number.isFinite(v) && (next[k] == null || v > next[k])) next[k] = v;
            }
            return next;
          });
        } else { setNoResp(true); }
      } catch (e) { console.debug('live-data poll skipped:', e); }
      n++;
      setTick((x) => x + 1);
      if (!stop) timer = setTimeout(tick, intervalRef.current);
    };
    tick();
    return () => { stop = true; clearTimeout(timer); setLive(false); };
  }, [streaming, manager]);

  const d = data || {};
  const overRedline = live && d.rpm != null && Number.isFinite(d.rpm) && redline > 0 && d.rpm >= redline;
  const resetPeaks = () => { setPeaks({}); toast.info('Peak hold reset'); };

  return (
    <div className="flex flex-col gap-2" data-testid="livedata-panel">
      {!connected && (
        <div className="flex items-center gap-2 text-sm text-slate-300 bg-[#0B0F19] border border-[#1E2A42] rounded-lg px-3 py-2" data-testid="livedata-connect-hint">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
          Connect an ECU (ignition ON) to stream live sensor data.
        </div>
      )}
      {connected && noResp && !live && (
        <div className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2" data-testid="livedata-noresp-hint">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          ECU connected but no live-data block responded (tried 0x1100/0x1300/0x1700/0xD100/0xD000).
        </div>
      )}

      <div data-testid="scan-block-control" style={{ display: 'none' }}>
        <select value={scanAddr} onChange={(e) => setScanAddr(Number(e.target.value))} data-testid="scan-block-select" />
      </div>

      {/* Shift-light alert banner */}
      {overRedline && (
        <div className="flex items-center justify-center gap-3 text-lg font-bold text-white bg-[#E53935] rounded-lg px-4 py-3 animate-pulse shadow-[0_0_30px_-4px_rgba(229,57,53,0.8)]" data-testid="shift-alert">
          <Zap className="h-6 w-6" /> SHIFT! · REDLINE {redline} RPM · {Math.round(d.rpm)} rpm
        </div>
      )}

      {/* Engine RPM dial (left) · detail table + chart (right) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 items-start">
      <div className="flex flex-col gap-3">
      <div className={`${winCard} flex flex-col`} data-testid="livedata-rpm-window">
        <div className={winHead}>
          <span className={titleBar}>Digital Dash</span>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-slate-400" data-testid="poll-rate-control">
              Rate
              <select value={interval} onChange={(e) => setInterval(Number(e.target.value))} data-testid="poll-rate-select"
                className="px-2 py-1 rounded bg-[#05070A] border border-[#1E2A42] text-slate-200 font-mono text-xs">
                {POLL_OPTIONS.map((ms) => <option key={ms} value={ms}>{ms} ms</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-400" data-testid="dash-theme-control">
              Theme
              <select value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="dash-theme-select"
                className="px-2 py-1 rounded bg-[#05070A] border border-[#1E2A42] text-slate-200 text-xs">
                {Object.entries(THEMES).map(([k, t]) => <option key={k} value={k}>{t.name}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-400" data-testid="shift-redline-control">
              <Zap size={13} className="text-[#E53935]" /> Shift @
              <input type="number" min="1000" max="15000" step="250" value={redline}
                onChange={(e) => setRedline(Math.max(0, Number(e.target.value) || 0))} data-testid="shift-redline-input"
                className="w-[74px] px-2 py-1 rounded bg-[#05070A] border border-[#1E2A42] text-slate-200 font-mono text-xs" />
              rpm
            </label>
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded ${live ? 'text-[#00F59B] bg-[#00F59B]/10' : 'text-slate-500 bg-slate-500/10'}`} data-testid="livedata-status">
              <span className={`h-2 w-2 rounded-full ${live ? 'bg-[#00F59B] animate-pulse' : 'bg-slate-600'}`} />
              {live ? 'LIVE' : connected ? (noResp ? 'NO DATA' : 'IDLE') : 'OFFLINE'}
            </span>
          </div>
        </div>
        <div className="p-4 flex items-center justify-center">
          <div className="w-full max-w-[440px] aspect-square">
            <AnalogTach value={d.rpm} peak={peaks.rpm} redline={redline} alert={d.rpm != null && redline > 0 && d.rpm >= redline} dur={interval} afr={(o2 && o2.o2v != null) ? Math.max(10, Math.min(18, 14.7 - (o2.o2v - 0.45) * 6)) : null} />
          </div>
        </div>
        <div className="flex items-center gap-3 px-3 py-2 border-t border-[#1E2A42] bg-[#0B0F19] text-xs" data-testid="livedata-peaks">
          <TrendingUp size={14} className="text-[#FFB800]" />
          <span className="text-slate-400">PEAK RPM <b className="text-[#FFB800] font-mono ml-1" data-testid="peak-rpm">{fmt(peaks.rpm, 0)}</b></span>
          <span className="text-slate-400">PEAK ENGINE T <b className="text-[#FFB800] font-mono ml-1" data-testid="peak-ect">{fmt(peaks.ect, 0)}{peaks.ect != null ? '°C' : ''}</b></span>
          <button data-testid="peak-reset-button" onClick={resetPeaks}
            className="ml-auto inline-flex items-center gap-1.5 px-2 py-1 rounded text-slate-400 hover:text-[#00F59B] hover:bg-[#00F59B]/10 transition">
            <RotateCcw size={13} /> Reset
          </button>
        </div>
      </div>

      {/* O2 · Fuel Trim — sits under the gauge dial, aligned with it (kiri-kanan) */}
      <div className={winCard} data-testid="o2-panel">
        <div className={winHead}>
          <span className={titleBar}>O2 · Fuel Trim (AFR)</span>
          <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer" data-testid="o2-toggle-label">
            <input type="checkbox" checked={o2on} onChange={(e) => setO2on(e.target.checked)} data-testid="o2-monitor-toggle" className="accent-[#00E5FF]" />
            {o2on ? 'Monitoring 0x2000' : 'Enable O2 read'}
          </label>
        </div>
        {!o2on ? (
          <div className="px-3 py-4 text-xs text-slate-500" data-testid="o2-disabled-hint">O2 read off (keeps K-Line lighter). Enable to monitor AFR while tuning.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3">
            <div className="rounded-lg bg-[#05070A] border border-[#1E2A42] px-3 py-3" data-testid="o2-voltage">
              <div className="text-[11px] text-slate-500 mb-1">O2 Sensor Voltage</div>
              <div className="text-2xl font-mono font-bold text-[#A78BFA]">{fmt(o2 && o2.o2v, 3)} <span className="text-sm text-slate-500">V</span></div>
            </div>
            <div className="rounded-lg bg-[#05070A] border border-[#1E2A42] px-3 py-3" data-testid="o2-stft">
              <div className="text-[11px] text-slate-500 mb-1">Short-Term Fuel Trim</div>
              <div className="text-2xl font-mono font-bold text-[#00E5FF]">{fmt(o2 && o2.stft, 3)}
                <span className="text-sm text-slate-500 ml-2">{o2 && o2.trimPct != null ? `${o2.trimPct > 0 ? '+' : ''}${o2.trimPct}%` : ''}</span>
              </div>
            </div>
            <div className="rounded-lg bg-[#05070A] border border-[#1E2A42] px-3 py-3 flex flex-col justify-center items-center" data-testid="o2-afr-status">
              <div className="text-[11px] text-slate-500 mb-1">Mixture</div>
              {(() => {
                const t = o2 && o2.trimPct;
                if (t == null) return <div className="text-lg font-bold text-slate-500">--</div>;
                if (t > 2) return <div className="text-lg font-bold text-[#FF6b63]" data-testid="afr-lean">LEAN ▲ (adding fuel)</div>;
                if (t < -2) return <div className="text-lg font-bold text-[#00F59B]" data-testid="afr-rich">RICH ▼ (cutting fuel)</div>;
                return <div className="text-lg font-bold text-[#00E5FF]" data-testid="afr-stoich">CLOSED-LOOP ✓</div>;
              })()}
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Right column: Engine table + live chart stacked */}
      <div className="flex flex-col gap-3">
        <div className={winCard} data-testid="livedata-values-window">
          <div className={winHead}>
            <span className={titleBar}>Engine</span>
            <button data-testid="livedata-pause-button" onClick={() => setPaused((p) => !p)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded text-slate-300 hover:bg-[#1E2A42]/60 transition">
              {paused ? <Play size={13} className="text-[#00F59B]" /> : <Pause size={13} className="text-[#FF6b63]" />}
              {paused ? 'Resume' : 'Pause'}
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-[#1E2A42]">
                <th className="font-semibold px-3 py-2">Title</th>
                <th className="font-semibold px-3 py-2">Value</th>
                <th className="font-semibold px-3 py-2 text-right">Peak</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {TABLE_ROWS.map((k) => {
                const m = METRICS[k];
                return (
                  <tr key={k} className="border-b border-[#131b29] last:border-0 hover:bg-[#0B0F19]/60" data-testid={`row-${k}`}>
                    <td className="px-3 py-2 text-slate-400">{m.label}</td>
                    <td className="px-3 py-2 text-slate-100 font-semibold">{fmt(d[k], m.dec)} <span className="text-slate-500">{m.unit}</span></td>
                    <td className="px-3 py-2 text-right text-[#FFB800]/80">{fmt(peaks[k], m.dec)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={winCard} data-testid="livedata-chart-window">
          <div className={winHead}>
            <span className={titleBar}>{METRICS[chartKey].label}</span>
            <select value={chartKey} onChange={(e) => setChartKey(e.target.value)} data-testid="livedata-chart-metric"
              className="text-xs font-semibold bg-[#05070A] border border-[#1E2A42] rounded px-2 py-1 text-slate-200">
              {['rpm', ...TABLE_ROWS].map((k) => <option key={k} value={k}>{METRICS[k].label}</option>)}
            </select>
          </div>
          <div className="p-2"><LiveChart history={histRef.current} metricKey={chartKey} /></div>
        </div>
        </div>
      </div>
    </div>
  );
};
